// RD-163: последовательное сохранение областей «Настроек».
// Без Vue: состояние передаётся объектом (в приложении — accessors к store и черновикам),
// API — адаптером. Подтверждённая операция сразу обновляет baseline и уходит из diff,
// ошибка независимой операции не останавливает остальные. Общей транзакции нет.

import type { EmployeeStatisticsSettings, Position, Role } from '@/types'
import {
    type GlobalDraft,
    type GlobalKey,
    type GlobalSettings,
    type GlobalSettingsPatch,
    type LunchDraft,
    type LunchRule,
    type PositionDraft,
    type PositionValues,
    type RoleDraft,
    type RoleValues,
    type StatisticsDraft,
    globalPatch,
    lunchOperations,
    normalizeRole,
    positionPatch,
    prunePositionDraft,
    pruneGlobalDraft,
    pruneRoleDraft,
    pruneStatisticsDraft,
    reconcileLunchDraft,
    rolePatch,
    sortLunchRules,
    statisticsPatch,
} from './settingsModel'

export interface SettingsApi {
    getGlobal(): Promise<GlobalSettings>
    patchGlobal(data: GlobalSettingsPatch): Promise<Partial<GlobalSettings> | null>
    patchStatistics(data: StatisticsDraft): Promise<Partial<EmployeeStatisticsSettings> | null>
    getLunches(): Promise<LunchRule[]>
    deleteLunch(id: string): Promise<void>
    patchLunch(id: string, data: Partial<Omit<LunchRule, 'id'>>): Promise<LunchRule | null>
    createLunch(data: Omit<LunchRule, 'id'>): Promise<LunchRule | null>
    patchRole(id: number, data: RoleValues): Promise<Role | null>
    getPositions(): Promise<Position[]>
    patchPosition(id: string, data: PositionDraft): Promise<Partial<Position> | null>
}

/** Ошибка без ответа сервера: результат записи неизвестен. */
export function isUnknownOutcome(error: unknown): boolean {
    const candidate = error as { response?: unknown, isServerResponse?: boolean } | null
    if (candidate?.isServerResponse) return false
    return !candidate?.response
}

export interface OperationError {
    id: string
    block: string
    message: string
    unknown?: boolean
}

export interface SaveOutcome {
    errors: OperationError[]
    /** Успешная запись, после которой не удалось актуализировать данные. */
    warnings: string[]
}

/* ---------- Global ---------- */

export interface GlobalState {
    global: GlobalSettings | null
    globalDraft: GlobalDraft
    /** После ошибки записи не удалось перечитать: запись этой области заблокирована до успешного GET. */
    globalNeedsReload: boolean
}

export async function saveGlobalPart(
    state: GlobalState,
    keys: ReadonlyArray<GlobalKey>,
    block: string,
    api: SettingsApi,
    outcome: SaveOutcome,
): Promise<void> {
    if (!state.global) return
    const patch = globalPatch(state.global, state.globalDraft, keys)
    if (!Object.keys(patch).length) return

    if (state.globalNeedsReload) {
        outcome.errors.push({ id: 'global', block, message: 'Результат прошлого сохранения неизвестен. Повторите загрузку настроек перед сохранением.', unknown: true })
        return
    }

    try {
        const body = await api.patchGlobal(patch)
        state.global = { ...state.global, ...patch, ...(body ?? {}) }
        state.globalDraft = pruneGlobalDraft(state.global, state.globalDraft)
    } catch {
        // Сервер пишет allowlist последовательно: часть полей могла сохраниться.
        try {
            state.global = await api.getGlobal()
            state.globalDraft = pruneGlobalDraft(state.global, state.globalDraft)
            if (Object.keys(globalPatch(state.global, state.globalDraft, keys)).length) {
                outcome.errors.push({ id: 'global', block, message: 'Не все параметры сохранены. Значения перечитаны с сервера, несохранённые правки остались.' })
            }
        } catch {
            state.globalNeedsReload = true
            outcome.errors.push({ id: 'global', block, message: 'Результат сохранения неизвестен: не удалось перечитать настройки. Повторите загрузку.', unknown: true })
        }
    }
}

/* ---------- Employee statistics ---------- */

export interface StatisticsState {
    statistics: EmployeeStatisticsSettings | null
    statisticsDraft: StatisticsDraft
}

export async function saveStatistics(state: StatisticsState, api: SettingsApi, outcome: SaveOutcome): Promise<void> {
    if (!state.statistics) return
    const patch = statisticsPatch(state.statistics, state.statisticsDraft)
    if (!Object.keys(patch).length) return

    try {
        const body = await api.patchStatistics(patch)
        state.statistics = { ...state.statistics, ...patch, ...(body ?? {}) }
        state.statisticsDraft = pruneStatisticsDraft(state.statistics, state.statisticsDraft)
    } catch {
        outcome.errors.push({ id: 'statistics', block: 'Показатели сотрудника', message: 'Не удалось сохранить показатели сотрудника.' })
    }
}

/* ---------- Lunch rules ---------- */

export interface LunchState {
    lunches: LunchRule[] | null
    lunchDraft: LunchDraft
    /**
     * reload — после ошибки не удалось перечитать список: очередь обедов заблокирована до успешного GET.
     * verify — результат POST неизвестен и в списке есть новые правила: нужна сверка пользователем.
     */
    lunchBlocked: null | 'reload' | 'verify'
}

const LUNCH_BLOCK = 'Обеденные перерывы'

export async function saveLunches(state: LunchState, api: SettingsApi, outcome: SaveOutcome): Promise<void> {
    if (!state.lunches) return
    const operations = lunchOperations(state.lunches, state.lunchDraft)
    if (!operations.length) return

    const blocked = state.lunchBlocked === 'reload'
        || (state.lunchBlocked === 'verify' && operations.some((operation) => operation.type === 'create'))
    if (blocked) {
        outcome.errors.push({
            id: 'lunch',
            block: LUNCH_BLOCK,
            message: state.lunchBlocked === 'reload'
                ? 'Список правил не перечитан после ошибки. Повторите загрузку перед сохранением.'
                : 'Проверьте список правил и подтвердите сверку перед повтором.',
            unknown: true,
        })
        return
    }

    let createdWithoutId = false

    for (const operation of operations) {
        try {
            if (operation.type === 'delete') {
                await api.deleteLunch(operation.id)
                state.lunches = state.lunches.filter((rule) => rule.id !== operation.id)
                state.lunchDraft = { ...state.lunchDraft, deleted: state.lunchDraft.deleted.filter((id) => id !== operation.id) }
            } else if (operation.type === 'patch') {
                const body = await api.patchLunch(operation.id, operation.data)
                state.lunches = sortLunchRules(state.lunches.map((rule) => (
                    rule.id === operation.id ? { ...rule, ...operation.data, ...(body ?? {}), id: rule.id } : rule
                )))
            } else {
                const body = await api.createLunch(operation.data)
                if (body?.id) {
                    state.lunches = sortLunchRules([...state.lunches, body])
                    state.lunchDraft = {
                        ...state.lunchDraft,
                        rows: state.lunchDraft.rows.map((row) => (row.key === operation.rowKey ? { ...row, id: body.id } : row)),
                    }
                } else {
                    // Создание подтверждено, но ID нет: строку убираем, правило придёт из GET ниже.
                    createdWithoutId = true
                    state.lunchDraft = { ...state.lunchDraft, rows: state.lunchDraft.rows.filter((row) => row.key !== operation.rowKey) }
                }
            }
        } catch (error) {
            const unknownCreate = operation.type === 'create' && isUnknownOutcome(error)
            const message = operation.type === 'delete'
                ? 'Не удалось удалить правило.'
                : operation.type === 'patch' ? 'Не удалось изменить правило.' : 'Не удалось создать правило.'
            await reloadLunchesAfterError(state, api, outcome, message, unknownCreate)
            return
        }
    }

    if (createdWithoutId) await refreshLunches(state, api, outcome)
}

async function reloadLunchesAfterError(state: LunchState, api: SettingsApi, outcome: SaveOutcome, message: string, unknownCreate: boolean) {
    try {
        const fresh = await api.getLunches()
        const previous = state.lunches ?? []
        const { draft, newServerIds } = reconcileLunchDraft(previous, fresh, state.lunchDraft)
        state.lunches = sortLunchRules(fresh)
        state.lunchDraft = draft
        if (unknownCreate && newServerIds.length) {
            state.lunchBlocked = 'verify'
            outcome.errors.push({
                id: 'lunch',
                block: LUNCH_BLOCK,
                message: 'Результат создания правила неизвестен: в списке появились правила, которые нельзя надёжно сопоставить. Проверьте список и удалите лишнее перед повтором.',
                unknown: true,
            })
            return
        }
        outcome.errors.push({
            id: 'lunch',
            block: LUNCH_BLOCK,
            message: `${message} Остальные правки обедов не отправлены, список перечитан.`,
        })
    } catch {
        state.lunchBlocked = 'reload'
        outcome.errors.push({
            id: 'lunch',
            block: LUNCH_BLOCK,
            message: `${message} Не удалось перечитать список правил — результат неизвестен. Повторите загрузку.`,
            unknown: true,
        })
    }
}

async function refreshLunches(state: LunchState, api: SettingsApi, outcome: SaveOutcome) {
    try {
        const fresh = await api.getLunches()
        const { draft } = reconcileLunchDraft(state.lunches ?? [], fresh, state.lunchDraft)
        state.lunches = sortLunchRules(fresh)
        state.lunchDraft = draft
    } catch {
        state.lunchBlocked = 'reload'
        outcome.warnings.push('Правило создано, но список не обновился. Повторите загрузку обеденных перерывов.')
    }
}

/* ---------- Roles ---------- */

export interface RoleState {
    role: Role
    baseline: RoleValues
    draft: RoleDraft
}

export async function saveRole(state: RoleState, api: SettingsApi, outcome: SaveOutcome): Promise<void> {
    const patch = rolePatch(state.baseline, state.draft)
    if (!Object.keys(patch).length) return

    try {
        const body = await api.patchRole(state.role.id, patch)
        state.role = body?.id === state.role.id ? body : { ...state.role, ...patch } as Role
        state.baseline = { ...state.baseline, ...normalizeRole(state.role) }
        state.draft = pruneRoleDraft(state.baseline, state.draft)
    } catch {
        outcome.errors.push({ id: `role-${state.role.id}`, block: 'Роль', message: `Не удалось сохранить роль «${state.role.name}».` })
    }
}

/* ---------- Positions ---------- */

export interface PositionsState {
    positions: Position[] | null
    baselines: Record<string, PositionValues>
    drafts: Record<string, PositionDraft>
}

const hasPositionFields = (body: Partial<Position> | null): body is Partial<Position> & PositionValues =>
    Boolean(body && 'short_name' in body && 'shift_schedule' in body)

export async function savePositions(state: PositionsState, api: SettingsApi, outcome: SaveOutcome): Promise<void> {
    if (!state.positions) return
    let needsRefresh = false
    const confirmed: Record<string, PositionDraft> = {}

    for (const position of state.positions) {
        const baseline = state.baselines[position.id]
        const draft = state.drafts[position.id]
        if (!baseline || !draft) continue
        const patch = positionPatch(baseline, draft)
        if (!Object.keys(patch).length) continue

        try {
            const body = await api.patchPosition(position.id, patch)
            confirmed[position.id] = patch
            if (hasPositionFields(body)) {
                state.baselines[position.id] = { short_name: body.short_name ?? null, shift_schedule: body.shift_schedule ?? null }
            } else {
                // Snapshot Position гарантирует только id/name: актуализируем существующим GET ниже.
                state.baselines[position.id] = { ...baseline, ...patch }
                needsRefresh = true
            }
            state.drafts[position.id] = prunePositionDraft(state.baselines[position.id], draft)
        } catch {
            outcome.errors.push({ id: `position-${position.id}`, block: 'Должности', message: `Не удалось сохранить должность «${position.name}».` })
        }
    }

    if (!needsRefresh) return
    try {
        const fresh = await api.getPositions()
        fresh.forEach((item) => {
            if (!(item.id in confirmed)) return
            state.baselines[item.id] = { short_name: item.short_name ?? null, shift_schedule: item.shift_schedule ?? null }
            state.drafts[item.id] = prunePositionDraft(state.baselines[item.id], state.drafts[item.id] ?? {})
        })
    } catch {
        outcome.warnings.push('Должности сохранены, но список не обновился. Перезагрузите раздел, чтобы увидеть данные сервера.')
    }
}
